// CanvasColorPickOverlay — IN-CANVAS eyedropper.
//
// Unlike ColorPickOverlay (which blows the image up fullscreen on a dark
// backdrop), this samples colours DIRECTLY on top of the selected image box,
// right where it sits on the canvas. The image is NOT enlarged — a transparent
// crosshair capture layer is placed over the box viewport; clicking maps the
// screen point onto the image's NATURAL pixels and reads the colour.
//
// Geometry mirrors CanvasCropOverlay:
//   - box container DOM id : `box-{boxId}`   (the clipped viewport of the box)
//   - image element DOM id : `{boxId}-image` (actual <img>, may overflow box)
// The capture layer is re-glued to the box every animation frame so it tracks
// scroll/zoom while open.
//
// Sampling is done from a fetched blob (createImageBitmap → offscreen canvas) so
// the canvas is never CORS-tainted when the image is served from a remote origin.

import { useState, useRef, useEffect, useCallback, useLayoutEffect } from "react";
import i18n from "i18next";
import { credentialsFor } from "../../../common/utils";
import { LuX } from "react-icons/lu";

const toHex = (r, g, b) => "#" + [r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("");

export default function CanvasColorPickOverlay({ boxId, imageUrl, onPicked, onClose }) {
  const [boxRect, setBoxRect] = useState(null); // live box viewport rect (screen px)
  const [ready, setReady] = useState(false);
  const [hover, setHover] = useState(null); // { x, y, color } in viewport px

  const boxElRef = useRef(null);
  const imgElRef = useRef(null);
  const sampleRef = useRef(null); // offscreen canvas at natural size

  // Locate the box + image DOM elements.
  useLayoutEffect(() => {
    const boxEl = document.getElementById(`box-${boxId}`);
    const imgEl =
      document.getElementById(`${boxId}-image`) ||
      (boxEl && boxEl.querySelector("img.basicImageClass")) ||
      (boxEl && boxEl.querySelector("img"));
    boxElRef.current = boxEl;
    imgElRef.current = imgEl;
    if (boxEl) setBoxRect(boxEl.getBoundingClientRect());
  }, [boxId]);

  // Keep the capture layer glued to the box while the canvas scrolls / zooms.
  useEffect(() => {
    let raf;
    const sync = () => {
      const boxEl = boxElRef.current;
      if (boxEl) setBoxRect(boxEl.getBoundingClientRect());
      raf = requestAnimationFrame(sync);
    };
    raf = requestAnimationFrame(sync);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Build the offscreen sampling canvas from a CORS-safe blob.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(imageUrl, { mode: "cors", ...credentialsFor(imageUrl) });
        const blob = await resp.blob();
        const bitmap = await createImageBitmap(blob);
        if (cancelled) return;
        const canvas = document.createElement("canvas");
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        canvas.getContext("2d").drawImage(bitmap, 0, 0);
        if (typeof bitmap.close === "function") bitmap.close();
        sampleRef.current = canvas;
        setReady(true);
      } catch (err) {
        console.error("[CanvasColorPickOverlay] load failed:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [imageUrl]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Map a viewport point onto the rendered image → natural pixel → colour.
  const sampleAt = useCallback((clientX, clientY) => {
    const imgEl = imgElRef.current;
    const canvas = sampleRef.current;
    if (!imgEl || !canvas || !imgEl.naturalWidth) return null;
    const rect = imgEl.getBoundingClientRect();
    if (
      clientX < rect.left ||
      clientX > rect.right ||
      clientY < rect.top ||
      clientY > rect.bottom
    ) {
      return null;
    }
    const nx = Math.floor(((clientX - rect.left) / rect.width) * canvas.width);
    const ny = Math.floor(((clientY - rect.top) / rect.height) * canvas.height);
    const [r, g, b] = canvas.getContext("2d").getImageData(nx, ny, 1, 1).data;
    return toHex(r, g, b);
  }, []);

  const onMove = useCallback(
    (e) => {
      if (!ready) return;
      const color = sampleAt(e.clientX, e.clientY);
      setHover(color ? { x: e.clientX, y: e.clientY, color } : null);
    },
    [ready, sampleAt]
  );

  const onClick = useCallback(
    (e) => {
      if (!ready) return;
      const color = sampleAt(e.clientX, e.clientY);
      if (color) onPicked?.(color);
    },
    [ready, sampleAt, onPicked]
  );

  if (!boxRect) return null;

  return (
    <>
      {/* Transparent crosshair capture layer over the box — image not enlarged. */}
      <div
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
        onClick={onClick}
        style={{
          position: "fixed",
          left: boxRect.left,
          top: boxRect.top,
          width: boxRect.width,
          height: boxRect.height,
          cursor: ready ? "crosshair" : "wait",
          zIndex: 100000,
          userSelect: "none",
        }}
      />

      {/* Live colour swatch following the cursor */}
      {hover && (
        <div
          style={{
            position: "fixed",
            left: hover.x + 16,
            top: hover.y + 16,
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "rgba(0,0,0,0.7)",
            padding: "5px 9px",
            borderRadius: 8,
            pointerEvents: "none",
            zIndex: 100002,
          }}
        >
          <span
            style={{
              width: 18,
              height: 18,
              borderRadius: 4,
              border: "1px solid #fff",
              background: hover.color,
            }}
          />
          <span style={{ color: "#fff", fontSize: 12, fontFamily: '"Inter", sans-serif' }}>
            {hover.color}
          </span>
        </div>
      )}

      {/* Instruction + cancel banner, pinned just above the box. */}
      <div
        style={{
          position: "fixed",
          left: boxRect.left + boxRect.width / 2,
          top: boxRect.top - 34,
          transform: "translateX(-50%)",
          display: "flex",
          alignItems: "center",
          gap: 8,
          background: "rgba(0,0,0,0.75)",
          color: "#fff",
          padding: "4px 6px 4px 12px",
          borderRadius: 8,
          fontSize: 12,
          fontWeight: 600,
          whiteSpace: "nowrap",
          zIndex: 100002,
        }}
      >
        <span>{i18n.t("ClickToPickColor")}</span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClose?.();
          }}
          title={i18n.t("Cancel")}
          style={{
            background: "rgba(255,255,255,0.15)",
            border: "none",
            borderRadius: 6,
            width: 26,
            height: 26,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            cursor: "pointer",
          }}
        >
          <LuX size={16} />
        </button>
      </div>
    </>
  );
}
